<template>
  <main class="unbind-info-page">
    <AppNavBar :title="t('card.unbindCard')" background-color="#f7f7f7">
      <template #right>
        <button
          type="button"
          class="history-btn"
          @click="router.push('/card/unbind/list')"
        >
          <app-image
            src="@/assets/images/commonImage/history_icon.png"
            alt=""
          />
        </button>
      </template>
    </AppNavBar>

    <div class="unbind-info-page__content">
      <section class="notice-card">
        <div class="notice-card__line">
          <app-image src="@/assets/images/commonImage/mark_icon.png" alt="" />
          <span>{{ t("card.unBindCardText_1") }}</span>
        </div>
        <p>{{ t("card.unBindCardText_2") }}</p>
      </section>

      <section class="card-panel">
        <div
          class="bank-card"
          :style="{ backgroundImage: 'url(' + cardImage + ')' }"
        >
          <span v-if="card.bin" class="bank-card__bin">BIN {{ card.bin }}</span>
          <div class="bank-card__balance">
            <div>
              {{ balanceValue }} <small>{{ balanceCurrency }}</small>
            </div>
            <span>{{ card.scene || "" }} {{ t("card.prepaidCard") }}</span>
          </div>
        </div>

        <h2>{{ t("card.basicInfo") }}</h2>
        <div class="info-row">
          <div class="info-row__label">
            <span class="info-row__icon">
              <app-image src="@/assets/images/Bank-card-two.svg" alt="" />
            </span>
            <span>{{ t("card.cardNumber") }}</span>
          </div>
          <span>{{ maskedCardNumber }}</span>
        </div>
        <div class="info-row">
          <div class="info-row__label">
            <span class="info-row__icon">
              <app-image src="@/assets/images/User.svg" alt="" />
            </span>
            <span>{{ t("card.cardholder") }}</span>
          </div>
          <span>{{ cardholder }}</span>
        </div>
        <div class="info-row">
          <div class="info-row__label">
            <span class="info-row__icon">
              <app-image src="@/assets/images/Mail-open.svg" alt="" />
            </span>
            <span>{{ t("auth.bindEmail") }}</span>
          </div>
          <span>{{ card.email || "--" }}</span>
        </div>
      </section>

      <section class="impact-card">
        <h2>{{ t("card.unBindCardText_3") }}</h2>
        <p v-for="key in impactKeys" :key="key">
          <i />
          <span>{{ t(key) }}</span>
        </p>
      </section>

      <van-button
        block
        round
        color="#ef0027"
        class="unbind-submit"
        @click="startUnbind"
      >
        {{ t("card.unbindCard") }}
      </van-button>
    </div>

    <van-popup
      v-model:show="showAtmPasswordSetupDialog"
      class="unbind-atm-password-popup"
      :close-on-click-overlay="false"
    >
      <section class="unbind-atm-password-dialog">
        <div class="unbind-atm-password-dialog__content">
          <div class="unbind-atm-password-dialog__image">
            <app-image :src="atmPasswordSetupImage" fit="contain" alt="" />
          </div>
          <strong>{{ t("card.unbindAtmPasswordSetupTitle") }}</strong>
          <p>{{ t("card.unbindAtmPasswordSetupDesc") }}</p>
        </div>
        <div class="unbind-atm-password-dialog__actions">
          <button
            type="button"
            class="is-cancel"
            @click="showAtmPasswordSetupDialog = false"
          >
            {{ t("common.cancel") }}
          </button>
          <button type="button" class="is-confirm" @click="goSetAtmPassword">
            {{ t("card.unbindAtmPasswordSetupAction") }}
          </button>
        </div>
      </section>
    </van-popup>

    <van-popup
      v-model:show="showCardPinSetupDialog"
      class="unbind-card-pin-popup"
      :close-on-click-overlay="false"
    >
      <section class="unbind-card-pin-dialog">
        <div class="unbind-card-pin-dialog__content">
          <div class="unbind-card-pin-dialog__image">
            <app-image :src="cardPinSetupImage" fit="contain" alt="" />
          </div>
          <strong>{{ t("card.unbindCardPinSetupTitle") }}</strong>
          <p>{{ t("card.unbindCardPinSetupDesc") }}</p>
        </div>
        <div class="unbind-card-pin-dialog__actions">
          <button
            type="button"
            class="is-cancel"
            @click="showCardPinSetupDialog = false"
          >
            {{ t("common.cancel") }}
          </button>
          <button type="button" class="is-confirm" @click="goSetCardPin">
            {{ t("card.unbindCardPinSetupAction") }}
          </button>
        </div>
      </section>
    </van-popup>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :confirm-disabled="!passwordInput.trim()"
      @cancel="passwordInput = ''"
      @closed="passwordInput = ''"
      @confirm="confirmPassword"
    />

    <PhysicalSecondCheckPopup
      v-model:show="showPhysicalSecondCheckPopup"
      @confirm="confirmPhysicalSecondCheck"
    />
  </main>
</template>

<script setup>
import { computed, onActivated, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import PhysicalSecondCheckPopup from "@/components/PhysicalSecondCheckPopup.vue";
import { cardMyList } from "@/api/card";
import { payPwdCheck } from "@/api/auth";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import { useProfileStore } from "@/stores/profile";
import cardOne from "@/assets/images/cardImage/card_one.png";
import cardTwo from "@/assets/images/cardImage/card_two.png";
import cardThree from "@/assets/images/cardImage/card_three.png";
import atmPasswordSetupImage from "@/assets/images/cardImage/card-unbind-atm-password.png";
import cardPinSetupImage from "@/assets/images/cardImage/unbind-card-pin.png";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, "card-unbind-info", {}));
const card = ref(pageState.value.cardInfo || {});
let hasActivatedCardPage = false;
const passwordInput = ref("");
const showPasswordDialog = ref(false);
const showPhysicalSecondCheckPopup = ref(false);
const showAtmPasswordSetupDialog = ref(false);
const showCardPinSetupDialog = ref(false);
// TODO: Replace this temporary value with the ATM PIN status from the card API.
const isAtmPasswordSet = ref(false);
const impactKeys = [
  "card.unBindCardText_4",
  "card.unBindCardText_5",
  "card.unBindCardText_6",
];

function isTrue(value) {
  return (
    value === true ||
    value === 1 ||
    ["true", "1"].includes(String(value).trim().toLowerCase())
  );
}

const cardImage = computed(() => {
  if (Number(card.value.cardType || pageState.value.cardType) === 1)
    return cardThree;
  return card.value.channelType === "WASABI" ? cardOne : cardTwo;
});
const balanceValue = computed(
  () =>
    card.value.balance?.available ?? pageState.value.balanceAvailable ?? "0.00",
);
const balanceCurrency = computed(
  () =>
    card.value.balance?.currency || pageState.value.balanceCurrency || "USD",
);
const maskedCardNumber = computed(() => {
  const bin = card.value.bin || pageState.value.bin || "****";
  const last4 = card.value.last4 || pageState.value.last4 || "****";
  return bin + "-********-" + last4;
});
const cardholder = computed(
  () =>
    (
      String(card.value.firstName || "") +
      " " +
      String(card.value.lastName || "")
    ).trim() || "--",
);

const loadCard = async (force = false) => {
  if (
    (!force && card.value.id) ||
    !pageState.value.cardId ||
    !profileStore.platformAddress
  )
    return;
  try {
    const result = await cardMyList({
      address: profileStore.platformAddress,
      pageNum: 1,
      pageSize: 999,
    });
    const list = Array.isArray(result) ? result : result?.list || [];
    card.value = list.find(
      (item) => String(item?.id) === String(pageState.value.cardId),
    ) || { ...pageState.value };
  } catch (error) {
    card.value = { ...pageState.value };
  }
};

function startUnbind() {
  const isPhysicalCard = card.value.cardType === "PHYSICAL";
  const isVirtual = card.value.cardType === "VIRTUAL";
  if (isPhysicalCard) {
    showAtmPasswordSetupDialog.value = true;
    return;
  }
  if (
    isVirtual &&
    !String(card.value.pin || "").trim() &&
    card.value.secondCheck
  ) {
    showCardPinSetupDialog.value = true;
    return;
  }
  showPasswordDialog.value = true;
}

function goSetCardPin() {
  showCardPinSetupDialog.value = false;
  navigateWithState(router, "/card/pin", {
    namespace: "card-pin",
    payload: { cardId: card.value.id || pageState.value.cardId },
  });
}

function goSetAtmPassword() {
  showAtmPasswordSetupDialog.value = false;
  if (isTrue(card.value?.secondCheck)) {
    showPhysicalSecondCheckPopup.value = true;
    return;
  }
  navigateToAtm();
}

function navigateToAtm() {
  navigateWithState(router, "/card/atm", {
    namespace: "card-atm",
    payload: {
      cardId: card.value.id || pageState.value.cardId,
    },
  });
}

function confirmPhysicalSecondCheck() {
  showPhysicalSecondCheckPopup.value = false;
  navigateToAtm();
}

const confirmPassword = async () => {
  const payPwd = String(passwordInput.value || "").trim();
  if (!payPwd) return;
  const verified = await payPwdCheck({ payPwd });
  if (!verified) {
    showFailToast(t("auth.passwordError"));
    return;
  }
  showPasswordDialog.value = false;
  navigateWithState(router, "/card/unbind/handling", {
    namespace: "card-unbind-handling",
    payload: { cardId: card.value.id || pageState.value.cardId },
  });
};

onMounted(() => loadCard(true));
onActivated(() => {
  if (hasActivatedCardPage) {
    loadCard(true);
    return;
  }
  hasActivatedCardPage = true;
});
</script>

<style scoped lang="scss">
.unbind-info-page {
  min-height: var(--app-viewport-height);
  background: #f7f7f7;
}

.history-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    width: 24px;
    height: 24px;
  }
}

.unbind-info-page__content {
  padding: 14px 14px calc(20px + env(safe-area-inset-bottom));
}

.notice-card,
.card-panel,
.impact-card {
  border-radius: 16px;
  background: #ffffff;
}

.notice-card {
  padding: 14px 16px;
  border: 1px solid #d5e5ff;
  background: #e4eeff;
  color: var(--wp-color-theme);
  font-size: 14px;
  line-height: 22px;

  &__line {
    display: flex;
    align-items: flex-start;
    gap: 6px;

    .app-image {
      flex: 0 0 auto;
      width: 18px;
      height: 18px;
      margin-top: 2px;
    }
  }

  p {
    margin: 0 0 0 24px;
  }
}

.card-panel,
.impact-card {
  margin-top: 10px;
  padding: 16px;
}

.bank-card {
  position: relative;
  aspect-ratio: 933 / 585;
  overflow: hidden;
  border-radius: 12px;
  background-position: center;
  background-size: 100% 100%;
  color: #ffffff;

  &__bin {
    position: absolute;
    top: 22px;
    right: 18px;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
    text-shadow: 0 1px 3.8px rgba(0, 0, 0, 0.2);
  }

  &__balance {
    position: absolute;
    bottom: 6%;
    left: 4%;
    font-size: 16px;
    font-weight: 700;

    small {
      font-size: 12px;
      font-weight: 400;
    }

    span {
      display: block;
      margin-top: 3px;
      font-size: 11px;
      font-weight: 400;
    }
  }
}

h2 {
  margin: 20px 0 14px;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 50px;
  border-bottom: 1px solid #f4f5f6;
  gap: 12px;
  color: #000000;
  font-size: 14px;
  font-weight: 500;

  &:last-child {
    border-bottom: 0;
  }

  &__label {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333333;
    white-space: nowrap;
  }

  &__icon {
    display: inline-flex;
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #f4f5f6;

    .app-image {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }
  }

  > span:last-child {
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.impact-card {
  h2 {
    margin-top: 0;
    color: #0a152f;
  }

  p {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin: 16px 0 0;
    color: #132240;
    font-size: 13px;
    line-height: 21px;

    span {
      font-weight: 500;
    }

    i {
      flex: 0 0 auto;
      width: 6px;
      height: 6px;
      margin-top: 8px;
      border-radius: 50%;
      background: var(--wp-color-theme);
    }
  }
}

.unbind-submit {
  margin-top: 16px;
}

.unbind-atm-password-popup {
  width: 300px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
}

.unbind-card-pin-popup {
  width: 300px;
  overflow: hidden;
  border-radius: 20px;
  background: #ffffff;
}

.unbind-card-pin-dialog {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 20px 16px;
  text-align: center;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    strong,
    p {
      width: 100%;
      margin: 0;
      color: #333333;
      font-size: 16px;
    }

    strong {
      font-weight: 600;
      line-height: 20px;
    }

    p {
      font-weight: 400;
      line-height: 22px;
    }
  }

  &__image {
    width: 70px;
    height: 70px;

    .app-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;

    button {
      width: 100%;
      height: 40px;
      border: 0;
      border-radius: 20px;
      font-size: 16px;
      font-weight: 500;
      line-height: 40px;
    }

    .is-cancel {
      background: #f0f0f0;
      color: #333333;
    }

    .is-confirm {
      background: #2667ff;
      color: #ffffff;
    }
  }
}

.unbind-atm-password-dialog {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 20px 16px;
  text-align: center;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    strong,
    p {
      width: 100%;
      margin: 0;
      color: #333333;
      font-size: 16px;
    }

    strong {
      font-weight: 600;
      line-height: 20px;
    }

    p {
      font-weight: 400;
      line-height: 22px;
    }
  }

  &__image {
    position: relative;
    width: 70px;
    height: 70px;
    overflow: hidden;

    .app-image {
      position: absolute;
      top: -12px;
      left: -15px;
      width: 100px;
      height: 100px;
      max-width: none;
      object-fit: contain;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;

    button {
      width: 100%;
      height: 40px;
      border: 0;
      border-radius: 20px;
      font-size: 16px;
      font-weight: 500;
      line-height: 40px;
    }

    .is-cancel {
      background: #f0f0f0;
      color: #333333;
    }

    .is-confirm {
      background: #2667ff;
      color: #ffffff;
    }
  }
}
</style>
